import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  PenTool, ArrowRight, Loader2, Zap, HelpCircle, Target, 
  Lightbulb, Heart, BookOpen, ArrowLeft,
  Briefcase, BookMarked, GraduationCap, Menu, X
} from 'lucide-react';

// Hook pour détecter la taille de l'écran
const useScreenSize = () => {
  const [screenSize, setScreenSize] = React.useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  });

  React.useEffect(() => {
    const handleResize = () => {
      setScreenSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = screenSize.width < 768;
  const isTablet = screenSize.width >= 768 && screenSize.width < 1024;
  const isDesktop = screenSize.width >= 1024;

  return { isMobile, isTablet, isDesktop, width: screenSize.width, height: screenSize.height };
};

// Composant de sélection de type d'objectif - Version Mobile
const MobileObjectiveTypeSelector = ({ objectiveTypes, selectedType, onTypeSelect, showTips, onToggleTips }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const selectedTypeData = objectiveTypes.find(type => type.id === selectedType);

  return (
    <div className="mb-6">
      <div className="text-center mb-4">
        <p className="text-sm font-medium text-gray-700">Parlez-moi de votre ambition… S'agit-il ?</p>
      </div>
      
      {/* Sélecteur mobile avec dropdown */}
      <div className="relative">
        <button
          type="button"
          onClick={() => setShowDropdown(!showDropdown)}
          className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${
            selectedTypeData 
              ? `${selectedTypeData.color} border-transparent` 
              : 'bg-white border-gray-200'
          }`}
        >
          <div className="flex items-center gap-3">
            {selectedTypeData?.icon}
            <span className="font-medium">{selectedTypeData?.label || 'Choisir un type'}</span>
          </div>
          <Menu className={`h-5 w-5 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
        </button>
        
        {showDropdown && (
          <>
            <div 
              className="fixed inset-0 z-10" 
              onClick={() => setShowDropdown(false)}
            />
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-20 max-h-64 overflow-y-auto">
              {objectiveTypes.map((type) => (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => {
                    onTypeSelect(type.id);
                    setShowDropdown(false);
                    onToggleTips(true);
                  }}
                  className={`w-full flex items-center gap-3 p-4 text-left transition-colors first:rounded-t-xl last:rounded-b-xl border-b border-gray-100 last:border-b-0 ${
                    selectedType === type.id 
                      ? 'bg-blue-50 text-blue-700 font-medium' 
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {type.icon}
                  <span>{type.label}</span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Composant de sélection de type d'objectif - Version Desktop/Tablette
const DesktopObjectiveTypeSelector = ({ objectiveTypes, selectedType, onTypeSelect, onToggleTips, isMobile, isTablet }) => {
  return (
    <div className="mb-6">
      <div className="text-center mb-4">
        <p className="text-sm font-medium text-gray-700">Parlez-moi de votre ambition… S'agit-il ?</p>
      </div>
      <div className={`grid gap-3 ${
        isMobile ? 'grid-cols-2' : 
        isTablet ? 'grid-cols-3' : 
        'grid-cols-6'
      }`}>
        {objectiveTypes.map((type) => (
          <button
            key={type.id}
            type="button"
            onClick={() => {
              onTypeSelect(type.id);
              onToggleTips(true);
            }}
            className={`flex items-center justify-center gap-2 ${isMobile ? 'py-3 px-2' : 'py-3 px-2'} rounded-xl border-2 transition-all ${
              selectedType === type.id 
                ? `${type.color} border-transparent` 
                : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
            }`}
            aria-pressed={selectedType === type.id}
          >
            {type.icon}
            <span className={`font-medium ${isMobile ? 'text-sm' : ''}`}>{type.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

/**
 * Composant simplifié de formulation d'objectif - Version Responsive
 */
const ObjectifFormulation = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const { isMobile, isTablet, isDesktop } = useScreenSize();
  
  // États du formulaire
  const [goal, setGoal] = useState('');
  const [selectedType, setSelectedType] = useState('project');
  const [showTips, setShowTips] = useState(false);
  
  // Types d'objectifs disponibles
  const objectiveTypes = [
    { 
      id: 'project', 
      label: 'Projet', 
      icon: <Target className="h-5 w-5" />, 
      color: 'bg-purple-100 text-purple-700'
    },
    { 
      id: 'idea', 
      label: 'Idée', 
      icon: <Lightbulb className="h-5 w-5" />, 
      color: 'bg-yellow-100 text-yellow-700'
    },
    { 
      id: 'skill', 
      label: 'Compétence', 
      icon: <GraduationCap className="h-5 w-5" />, 
      color: 'bg-green-100 text-green-700'
    },
    { 
      id: 'career', 
      label: 'Carrière', 
      icon: <Briefcase className="h-5 w-5" />, 
      color: 'bg-blue-100 text-blue-700'
    },
    { 
      id: 'learning', 
      label: 'Apprentissage', 
      icon: <BookMarked className="h-5 w-5" />, 
      color: 'bg-indigo-100 text-indigo-700'
    },
    { 
      id: 'desire', 
      label: 'Désir', 
      icon: <Heart className="h-5 w-5" />, 
      color: 'bg-pink-100 text-pink-700'
    }
  ];
  
  // Conseils de formulation selon le type d'objectif
  const formTips = {
    project: [
      "Quel est l'objectif principal de votre projet?",
      "Dans quel domaine s'inscrit-il (personnel, professionnel, éducatif)?",
      "Quel résultat concret souhaitez-vous obtenir?",
      "Quelles sont les étapes clés pour y parvenir?",
      "Quel est votre échéancier pour ce projet?"
    ],
    idea: [
      "Quelle est l'idée que vous aimeriez développer?",
      "Quel problème cherche-t-elle à résoudre?",
      "Qui pourrait bénéficier de cette idée?",
      "Quelles ressources seraient nécessaires pour concrétiser cette idée?",
      "Quels sont les défis potentiels à considérer?"
    ],
    desire: [
      "Qu'est-ce que vous souhaitez atteindre ou devenir?",
      "Pourquoi est-ce important pour vous?",
      "Quels changements cela apporterait dans votre vie?",
      "Quels obstacles pourraient vous empêcher d'y parvenir?",
      "Comment saurez-vous que vous avez atteint votre objectif?"
    ],
    skill: [
      "Quelle compétence souhaitez-vous développer ou améliorer?",
      "Pourquoi cette compétence est-elle importante pour vous?",
      "À quel niveau de maîtrise aspirez-vous?",
      "Comment comptez-vous mettre en pratique cette compétence?",
      "Quelles ressources pourriez-vous utiliser pour progresser?"
    ],
    career: [
      "Quel est votre objectif professionnel?",
      "Quelles étapes envisagez-vous pour y parvenir?",
      "Quelles compétences devrez-vous acquérir ou développer?",
      "Quelles opportunités recherchez-vous?",
      "Comment cet objectif s'inscrit-il dans votre plan de carrière global?"
    ],
    learning: [
      "Quel sujet ou domaine souhaitez-vous explorer?",
      "Quel niveau de connaissance ou d'expertise visez-vous?",
      "Comment comptez-vous appliquer ces connaissances?",
      "Quelles méthodes d'apprentissage vous conviennent le mieux?",
      "Quelles ressources avez-vous déjà identifiées pour cet apprentissage?"
    ]
  };
  
  // Gérer la soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!goal.trim() || isLoading || goal.length < 10) return;
    
    setIsLoading(true);
    
    try {
      navigate('/objectif-detail', { 
        state: { 
          objectif: goal.trim(),
          type: selectedType
        } 
      });
    } catch (error) {
      console.error("Erreur lors de la création de l'objectif:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-screen w-full bg-gradient-to-b from-purple-50 to-white">
      {/* Header responsive */}
      <header className={`bg-white shadow-sm ${isMobile ? 'py-3 px-4' : 'py-4 px-6'} w-full`}>
        <div className="w-full mx-auto flex justify-between items-center">
          <h1 className={`${isMobile ? 'text-lg' : 'text-2xl'} font-bold text-purple-700 flex items-center`}>
            <Target className={`${isMobile ? 'h-5 w-5' : 'h-6 w-6'} mr-2`} /> 
            {isMobile ? 'Ambition' : 'Formuler une ambition, une idée, un projet...'}
          </h1>
          <button 
            onClick={() => navigate('/')}
            className={`text-gray-600 hover:text-purple-700 flex items-center ${isMobile ? 'text-sm' : ''}`}
          >
            <ArrowLeft className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'} mr-1`} />
            <span>{isMobile ? '' : 'Retour'}</span>
          </button>
        </div>
      </header>
      
      {/* Main content responsive */}
      <div className={`flex-1 flex items-center justify-center w-full ${isMobile ? 'px-2 py-4' : 'px-4 py-6'}`}>
        <div className={`w-full mx-auto ${isDesktop ? 'max-w-4xl' : 'max-w-full'}`}>        
          <div className={`bg-white rounded-xl shadow-lg ${isMobile ? 'p-4' : isTablet ? 'p-6' : 'p-8'} relative`}>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Sélection du type d'objectif - Adaptatif */}
              {isMobile ? (
                <MobileObjectiveTypeSelector
                  objectiveTypes={objectiveTypes}
                  selectedType={selectedType}
                  onTypeSelect={setSelectedType}
                  showTips={showTips}
                  onToggleTips={setShowTips}
                />
              ) : (
                <DesktopObjectiveTypeSelector
                  objectiveTypes={objectiveTypes}
                  selectedType={selectedType}
                  onTypeSelect={setSelectedType}
                  onToggleTips={setShowTips}
                  isMobile={isMobile}
                  isTablet={isTablet}
                />
              )}
              
              {/* Champ de saisie de l'objectif - Responsive */}
              <div className="relative">
                <div className={`absolute ${isMobile ? 'left-3 top-3' : 'left-4 top-4'} flex items-center`}>
                  <PenTool className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'} text-gray-400`} />
                </div>
                <textarea
                  value={goal}
                  onChange={(e) => setGoal(e.target.value)}
                  placeholder={`Décrivez votre ${
                    selectedType === 'project' ? 'projet' : 
                    selectedType === 'idea' ? 'idée' : 
                    selectedType === 'desire' ? 'désir' :
                    selectedType === 'skill' ? 'compétence à acquérir' :
                    selectedType === 'career' ? 'objectif de carrière' : 'objectif d\'apprentissage'
                  } en quelques mots. Notez vos idées comme elles viennent.`}
                  className={`w-full ${
                    isMobile ? 'pl-10 pr-3 py-3 h-40 text-base' : 
                    'pl-12 pr-4 py-4 h-56 text-lg'
                  } rounded-xl border-2 border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-colors resize-none`}
                  required
                  aria-required="true"
                />
                <div className={`flex justify-between text-xs text-gray-500 mt-1 ${isMobile ? 'px-1' : 'px-2'}`}>
                  <span>{goal.length} caractères</span>
                  <span>{goal.length < 10 ? "Minimum 10 caractères recommandés" : "👍 Bonne longueur"}</span>
                </div>
              </div>
              
              {/* Guide de formulation - Responsive */}
              {showTips && selectedType && formTips[selectedType] && (
                <div className={`bg-blue-50 rounded-xl ${isMobile ? 'p-3' : 'p-4'} border border-blue-100`}>
                  <div className="flex items-start">
                    <Zap className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'} text-blue-600 flex-shrink-0 mt-0.5`} />
                    <div className="ml-2">
                      <h4 className={`font-medium text-blue-700 ${isMobile ? 'text-sm' : ''}`}>
                        Guide pour formuler votre {
                          selectedType === 'project' ? 'projet' : 
                          selectedType === 'idea' ? 'idée' : 
                          selectedType === 'desire' ? 'désir' :
                          selectedType === 'skill' ? 'objectif de compétence' :
                          selectedType === 'career' ? 'objectif de carrière' : 'objectif d\'apprentissage'
                        }
                      </h4>
                      <ul className={`mt-2 space-y-1 ${isMobile ? 'text-xs' : 'text-sm'} text-gray-700`}>
                        {formTips[selectedType].map((tip, index) => (
                          <li key={index} className="flex items-start">
                            <span className="mr-2">•</span>
                            <span>{tip}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Information sur la suite du processus - Responsive */}
              <div className={`bg-purple-50 rounded-xl ${isMobile ? 'p-3' : 'p-4'} border border-purple-100`}>
                <div className="flex items-start">
                  <Zap className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'} text-purple-600 flex-shrink-0 mt-0.5`} />
                  <div className="ml-2">
                    <h4 className={`font-medium text-purple-700 ${isMobile ? 'text-sm' : ''}`}>
                      Prêt à préciser votre ambition ?
                    </h4>
                    <p className={`mt-1 ${isMobile ? 'text-xs' : 'text-sm'} text-gray-700`}>
                      Notre assistant IA va vous accompagner dans une conversation personnalisée pour :
                    </p>
                    <ul className={`mt-1 space-y-1 ${isMobile ? 'text-xs' : 'text-sm'} text-gray-700`}>
                      <li className="flex items-center">
                        <span className="mr-2">•</span>
                        <span>Clarifier vos objectifs et motivations</span>
                      </li>
                      <li className="flex items-center">
                        <span className="mr-2">•</span>
                        <span>Identifier les étapes concrètes</span>
                      </li>
                      <li className="flex items-center">
                        <span className="mr-2">•</span>
                        <span>Générer des recommandations personnalisées</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              
              {/* Bouton de soumission - Responsive */}
              <div className="flex justify-center">
                <button
                  type="submit"
                  disabled={!goal.trim() || isLoading || goal.length < 10}
                  className={`flex items-center justify-center ${
                    isMobile ? 'px-6 py-3 text-base' : 'px-8 py-4 text-lg'
                  } rounded-xl font-medium transition-all duration-300 ${
                    !goal.trim() || isLoading || goal.length < 10
                      ? 'bg-gray-200 text-gray-500 cursor-not-allowed' 
                      : 'bg-gradient-to-r from-purple-600 to-indigo-700 text-white shadow-lg hover:shadow-xl hover:translate-y-[-2px]'
                  } ${isMobile ? 'w-full' : ''}`}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'} animate-spin mr-2`} />
                      {isMobile ? 'Démarrage...' : 'Démarrage en cours...'}
                    </>
                  ) : (
                    <>
                      {isMobile ? 'Commencer' : 'Commencer la conversation'} 
                      <ArrowRight className={`ml-2 ${isMobile ? 'h-4 w-4' : 'h-5 w-5'}`} />
                    </>
                  )}
                </button>
              </div>
              
              {/* Bouton d'aide flottant - Adaptatif */}
              {!isMobile && (
                <button 
                  type="button"
                  onClick={() => setShowTips(!showTips)}
                  className="absolute top-2 right-2 p-2 text-blue-600 hover:bg-blue-50 rounded-full"
                  aria-label={showTips ? "Masquer les conseils" : "Afficher les conseils"}
                >
                  <HelpCircle className="h-5 w-5" />
                </button>
              )}
            </form>
          </div>
          
          {/* Footer info - Responsive */}
          <div className={`${isMobile ? 'mt-3' : 'mt-4'} text-center ${isMobile ? 'text-xs' : 'text-sm'} text-gray-500`}>
            <p className="flex items-center justify-center">
              <BookOpen className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} mr-2 text-gray-400`} />
              {isMobile ? 'Conversation personnalisée' : 'Une conversation personnalisée vous attend pour préciser votre ambition'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ObjectifFormulation;